import { useEffect, useMemo } from 'react';
import { useMap } from 'react-map-gl';
import turfBbox from '@turf/bbox';
import { multiPoint } from '@turf/helpers';

/**
 * A 2-D bounding box in WGS84: `[minLng, minLat, maxLng, maxLat]` — the shape the
 * `master_data` BE emits per row as `bbox`.
 */
export type BBox = [number, number, number, number];

/**
 * Combines a collection of bounding boxes into the single box that encloses all
 * of them (the union extent). Returns `null` for an empty collection. Uses turf:
 * every box contributes its two corners to a `multiPoint`, and `@turf/bbox`
 * returns the enclosing box of all corners.
 *
 * @param {BBox[]} boxes - The bounding boxes to combine.
 * @return {BBox | null} The enclosing box, or `null` when there are none.
 */
export function unionBounds(boxes: BBox[]): BBox | null {
  if (boxes.length === 0) {
    return null;
  }
  const corners = boxes.flatMap(([minLng, minLat, maxLng, maxLat]) => [
    [minLng, minLat],
    [maxLng, maxLat],
  ]);
  return turfBbox(multiPoint(corners)) as BBox;
}

interface FitBoundsControlProps {
  /** Bounding boxes to frame together; the camera fits the union of all of them. */
  bounds: BBox[];
  /** Inner padding (px) around the fitted bounds. */
  padding?: number;
  /** Don't zoom past this when framing (avoids over-zoom on one small/point bbox). */
  maxZoom?: number;
  /** Animation duration (ms). */
  duration?: number;
}

/**
 * Headless child of `<Map>` that frames a collection of bounding boxes: whenever
 * `bounds` changes to a non-empty set, it animates the camera to fit their union
 * (`fitBounds`). An empty collection is a no-op (the camera stays put). Renders
 * nothing. A single (possibly zero-size point) bbox is framed at `maxZoom`.
 *
 * @param {FitBoundsControlProps} props - The bbox collection plus optional fit options.
 * @return {null} Always renders nothing.
 */
export function FitBoundsControl({
  bounds,
  padding = 60,
  maxZoom = 13,
  duration = 600,
}: FitBoundsControlProps) {
  const { current: map } = useMap();

  // A stable signature so the fit only re-fires when the bbox set actually
  // changes — the `bounds` array identity is caller-owned and may churn per render.
  const signature = bounds.map((b) => b.join(',')).join(';');

  const union = useMemo(
    () => unionBounds(bounds),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [signature]
  );

  useEffect(() => {
    if (!map || !union) {
      return;
    }
    map.fitBounds(
      [
        [union[0], union[1]],
        [union[2], union[3]],
      ],
      { padding, maxZoom, duration }
    );
  }, [map, union, padding, maxZoom, duration]);

  return null;
}
